import { Injectable, signal } from '@angular/core';
import { EthelPageContextValue, EthelPortalPageCode } from '../models/ethel.model';

/**
 * ADO 149676 (U6 · Ethel Employee Portal Surface), design.md §7.2.
 *
 * `providedIn: 'root'` is the load-bearing word in this file. `<app-ethel-host />` is
 * mounted in `app.html` as a SIBLING of `<router-outlet/>`, so it and anything it
 * injects resolve from the ROOT `EnvironmentInjector`. A component- or route-level
 * `providers:` entry builds a CHILD injector the root injector is not a descendant of —
 * `current()` would resolve `null` on every view, forever, and — because Rule 1 below is
 * fail-closed — that failure is SILENT and GREEN: AC-024, AC-U6-07 and AC-423's visible
 * half all assert absence, and all three pass vacuously against a feature that does not
 * exist. The console shipped exactly this defect (a route-provided `ETHEL_PAGE_CONTEXT`
 * token), found it in manual test, and DELETED the mechanism — see the console's own
 * `features/ethel/ethel-page-context.ts:25-28` and `ethel-record-context.service.ts:14-20`,
 * both of which carry doc comments written specifically to stop it being reintroduced.
 * Do not "simplify" this back to a `providers:` entry.
 *
 * `KISS` — one nullable signal instead of a token plus an optional boolean plus an
 * "is it even injectable here" question. `current() === null` IS suppression, teardown,
 * first paint and an un-annotated view, all at once — exactly the set of states the
 * fail-closed render rule (`EthelHostService.mounted`) wants to treat identically.
 *
 * Only the publisher half of the console's split context mechanism is built here
 * (`YAGNI`): the console also reads `area` off the Router because its areas ARE routes.
 * Seven of the portal's nine surfaces are in-page tab signals with no route to key on, so
 * a second, route-`data`-based mechanism would have nothing to do here.
 */
@Injectable({ providedIn: 'root' })
export class EthelPageContextService {
  private readonly _current = signal<EthelPageContextValue | null>(null);
  readonly current = this._current.asReadonly();

  /** Published by whichever view currently owns a portal page. */
  set(value: EthelPageContextValue): void {
    this._current.set(value);
  }

  /**
   * Clears only if the CURRENTLY published page is one of the caller's own codes.
   *
   * `/policies` hosts TWO publishers on one root signal: `PolicyLibraryPageComponent`
   * (My Library) and its child `ComplianceViewComponent` (My Compliance), toggled by one
   * `@if`. Angular runs a component's view effects before it updates its OWN template, so
   * flipping compliance → library runs the parent's effect (publishes `my_library_*`)
   * BEFORE the `@if` removes the child and fires its `ngOnDestroy` — an unconditional
   * `clear()` there wipes out the value the parent just set, and every one of the four
   * `my_library_*` surfaces never mounts. Scoping the clear to "only if I still own what's
   * published" makes the two publishers safe to coexist without either needing to know
   * the other exists.
   */
  clearIfOwned(pages: readonly EthelPortalPageCode[]): void {
    const cur = this._current();
    if (cur && pages.includes(cur.portalPage)) {
      this._current.set(null);
    }
  }
}
